import type { ReactNode } from 'react';
import InlineStatus from './InlineStatus';
import type { PortfolioCoverageSummary } from '../../lib/portfolioCoverageSummary';
import { extractPortfolioValueArrivalEvidence } from '../../lib/portfolioValueArrival';

interface PortfolioValueArrivalSummaryProps {
  valueLabel: string;
  summary: PortfolioCoverageSummary;
  action?: ReactNode;
  emphasis?: 'strong' | 'calm';
  className?: string;
  testId?: string;
}

export default function PortfolioValueArrivalSummary({
  valueLabel,
  summary,
  action,
  emphasis = 'strong',
  className = '',
  testId,
}: PortfolioValueArrivalSummaryProps) {
  const evidence = extractPortfolioValueArrivalEvidence(summary);

  if (!evidence) {
    return null;
  }

  return (
    <div data-testid={testId}>
      <InlineStatus
        tone={summary.tone}
        title={emphasis === 'strong' ? 'Why Pinecurl sent you here' : 'Why Pinecurl kept you in analytics'}
        message={(
          <div className="space-y-3">
            <div className="space-y-1">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">
              {emphasis === 'strong' ? 'Carried portfolio value' : 'Mounted analytics proof'}
              </p>
              <p className="font-mono tabular-nums text-2xl font-semibold text-white">
                {valueLabel}
              </p>
             </div>
             <p className="text-sm text-gray-300">
               {emphasis === 'strong'
                 ? evidence.isExchangeLed
                   ? `Pinecurl sent you here because ${evidence.sourceLabel} already mounted this portfolio value. Source, proof time, freshness, and coverage stay attached below before deeper review takes over.`
                   : `Pinecurl sent you here because ${evidence.sourceLabel} already mounted this portfolio value, so the same source and proof time stay visible below before deeper review takes over.`
                 : evidence.isExchangeLed
                   ? `Pinecurl kept this analytics view on the mounted ${evidence.sourceLabel} proof, with the same proof time, freshness, and coverage kept below while analytics stays the owner.`
                   : `Pinecurl kept this analytics view on the same loaded first-value proof from ${evidence.sourceLabel}, with the carried source and proof time kept visible below while analytics stays the owner.`}
             </p>
           </div>
         )}
        meta={(
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <span>
              {evidence.isExchangeLed ? 'Exchange source' : 'Source'}:{' '}
              <span className="font-mono tabular-nums text-gray-100">{evidence.sourceLabel}</span>
            </span>
            {evidence.completedLabel ? (
              <span>
                Proof time: <span className="font-mono tabular-nums text-gray-100">{evidence.completedLabel}</span>
              </span>
            ) : null}
            {evidence.freshnessLabel ? (
              <span>
                Freshness: <span className="font-mono tabular-nums text-gray-100">{evidence.freshnessLabel}</span>
              </span>
            ) : null}
            {evidence.coverageLabel ? (
              <span>
                Coverage: <span className="font-mono tabular-nums text-gray-100">{evidence.coverageLabel}</span>
              </span>
            ) : null}
          </div>
        )}
        action={action}
        className={`rounded-xl ${className}`.trim()}
      />
    </div>
  );
}
